/**
 * Theme V2 Types
 *
 * Defines TypeScript interfaces for ThemeV2 entity using V2 types
 * from @segmently/schema-models/v2.
 *
 * ThemeV2 contains:
 * - Backdrop, HeaderBar, ActionBar (V2 schema types)
 * - Colors, Fonts (using TypographyStyle), OptionsListSettings
 * - FlowDocument (V2 screen graph with StepNode screens)
 *
 * V2 differences from V1:
 * - TypographyStyle instead of LabelBlock
 * - `.appearance.*` paths instead of `.styles.*`
 * - `align: 'start'|'center'|'end'` instead of `textAlign: 'left'|'center'|'right'`
 * - Backdrop instead of BackgroundStyle
 * - ActionBar instead of Footer
 * - HeaderBar instead of NavigationBar
 * - FlowDocument instead of ScreensGraph
 *
 * @module themes/types
 */

import type {
  Backdrop,
  HeaderBar,
  ActionBar,
  FlowDocument,
  TypographyStyle,
  FontFamily,
  ToggleMarkKind,
  EdgeInsets,
  ScreenAnimation,
  ScreenTransition,
  StepKind,
} from '@segmently/schema-models/v2';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Add 50% alpha to hex color.
 * Used for subtitle colors which should be 50% of the base title color.
 */
export function addAlpha50(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  return hex.length === 6 ? `#${hex}80` : hexColor;
}

// ============================================================================
// Theme V2 Color Settings
// ============================================================================

/**
 * High-level color settings that propagate to V2 screen content.
 *
 * Same 3 color roles as V1, but propagation targets use V2 paths:
 * - buttonsBackground → content.actionBar.primary.appearance.backgroundColor
 * - titlesSubtitles → content.copy.title.appearance.color (100%), subtitle (50% alpha)
 * - buttonsTitle → content.actionBar.primary.content.appearance.color
 */
export interface ThemeV2Colors {
  /** Background color for buttons and input fields (e.g., '#ffc201') */
  buttonsBackground: string;
  /** Text color for titles and subtitles (e.g., '#ffffff') */
  titlesSubtitles: string;
  /** Text color for button labels (e.g., '#000000') */
  buttonsTitle: string;
}

// ============================================================================
// Theme V2 Font Settings
// ============================================================================

/**
 * Font settings using V2 TypographyStyle.
 *
 * Uses TypographyStyle instead of V1's LabelBlock. Key differences:
 * - `align` field ('start'|'center'|'end') instead of `textAlign` ('left'|'center'|'right')
 * - Additional fields: lineHeight, backgroundColor, backgroundColorOpacity, etc.
 *
 * Propagation hierarchy:
 * - main.fontFamily propagates to all text elements if not overridden
 * - title inherits fontFamily from main if not set
 * - subtitle inherits fontFamily from main if not set
 */
export interface ThemeV2Fonts {
  /** Main font settings — fontFamily propagates to all text */
  main: {
    fontFamily: FontFamily;
  };
  /** Title text styling (V2 TypographyStyle) */
  title: TypographyStyle;
  /** Subtitle text styling (V2 TypographyStyle) */
  subtitle: TypographyStyle;
}

// ============================================================================
// Theme V2 Options List Settings
// ============================================================================

/**
 * Options/list settings for V2 selection screens.
 *
 * Replaces V1 ThemeTableSettings. Uses V2 types:
 * - ToggleMarkKind ('square'|'circle') instead of V1's checkboxKind string union
 * - Single listBorderRadius (V2 uses BorderStyle.radius on OptionsListStyle)
 * - Item title/subtitle as TypographyStyle instead of LabelBlock
 *
 * Applies to V2 StepKinds:
 * - ListMultiPick, ListSinglePick
 * - GridMultiPick, GridSinglePick
 * - HeroList
 */
export interface ThemeV2OptionsListSettings {
  /** Checkbox shape for selection screens */
  checkboxKind: ToggleMarkKind;
  /** Border radius for the list container */
  listBorderRadius: number;
  /** List item title styling (V2 TypographyStyle) */
  itemTitle: TypographyStyle;
  /** List item subtitle styling (V2 TypographyStyle) */
  itemSubtitle: TypographyStyle;
}

// ============================================================================
// Theme V2 Layout Settings
// ============================================================================

/**
 * Spacing scale used by image-derived design tokens.
 *
 * These values are intentionally separate from component-specific settings so
 * extraction can preserve the observed system scale even when a token is not
 * yet projected to a renderer-backed schema path.
 */
export interface ThemeV2SpacingScale {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

/**
 * Radius scale used by image-derived design tokens.
 */
export interface ThemeV2RadiusScale {
  sm?: number;
  md?: number;
  lg?: number;
  full?: number;
}

export interface ThemeV2ButtonLayoutSettings {
  /** Uniform button corner radius in points */
  radius?: number;
  /** Button height in points */
  height?: number;
  /** Inner button padding */
  insets?: EdgeInsets;
}

export interface ThemeV2ActionBarLayoutSettings {
  /** Action bar container padding */
  insets?: EdgeInsets;
  /** Future renderer-backed gap between primary/secondary buttons */
  buttonGap?: number;
  primaryButton?: ThemeV2ButtonLayoutSettings;
  secondaryButton?: ThemeV2ButtonLayoutSettings;
}

export interface ThemeV2OptionsListLayoutSettings {
  /** Selection item corner radius in points */
  itemRadius?: number;
  /** Inner item padding */
  itemInsets?: EdgeInsets;
  /** List/frame padding */
  layoutInsets?: EdgeInsets;
  /** Gap between list/grid items */
  itemsSpacing?: number;
  /** Cell height as percentage of width for grid-like options */
  cellHeightFromCellWidth?: number;
  /** Use maximum cell height for all options */
  useMaxCellHeight?: boolean;
}

export interface ThemeV2MediaLayoutSettings {
  /** Featured media height as percentage of its container */
  featuredHeightPercentage?: number;
  /** Featured media uniform corner radius in points */
  cornerRadius?: number;
}

export interface ThemeV2FormLayoutSettings {
  /** Future renderer-backed field height in points */
  fieldHeight?: number;
  /** Future renderer-backed field corner radius in points */
  fieldRadius?: number;
  /** Future renderer-backed field inner padding */
  fieldInsets?: EdgeInsets;
}

export interface ThemeV2PickerLayoutSettings {
  /** Picker container corner radius in points */
  containerRadius?: number;
  /** Picker container padding */
  containerInsets?: EdgeInsets;
}

export interface ThemeV2ProgressLayoutSettings {
  /** Progress indicator/fill thickness in points */
  indicatorHeight?: number;
  /** Progress track thickness in points */
  trackHeight?: number;
  /** Progress container padding */
  containerInsets?: EdgeInsets;
}

export interface ThemeV2PaywallLayoutSettings {
  /** Product cell corner radius in points */
  productRadius?: number;
  /** Product cell inner padding */
  productInsets?: EdgeInsets;
  /** Gap between product cells */
  productSpacing?: number;
  /** Paywall footer padding */
  footerInsets?: EdgeInsets;
}

export interface ThemeV2WebEmbedLayoutSettings {
  /** Stored design-system token for embedded content padding */
  contentInsets?: EdgeInsets;
}

export interface ThemeV2FlexibleLayoutSettings {
  /** Flexible screen scroll/fixed container padding */
  containerInsets?: EdgeInsets;
  /** Default padding for sections inside a flexible layout */
  sectionPadding?: EdgeInsets;
  /** Default section corner radius */
  sectionRadius?: number;
  /** Default gap represented as section bottom margin */
  sectionSpacing?: number;
}

/**
 * Layout tokens extracted from references or screenshots.
 *
 * Phase 1 maps only renderer-backed paths. The remaining fields are stored as
 * stable contracts for Phase 2+ schema/render expansion and evals.
 */
export interface ThemeV2LayoutSettings {
  spacingScale?: ThemeV2SpacingScale;
  radiusScale?: ThemeV2RadiusScale;
  actionBar?: ThemeV2ActionBarLayoutSettings;
  optionsList?: ThemeV2OptionsListLayoutSettings;
  media?: ThemeV2MediaLayoutSettings;
  form?: ThemeV2FormLayoutSettings;
  picker?: ThemeV2PickerLayoutSettings;
  progress?: ThemeV2ProgressLayoutSettings;
  paywall?: ThemeV2PaywallLayoutSettings;
  webEmbed?: ThemeV2WebEmbedLayoutSettings;
  flexibleLayout?: ThemeV2FlexibleLayoutSettings;
}

/**
 * Compact, audit-only Figma provenance attached to themes imported via the
 * `/figma-theme` skill. These are references for traceability — never raw MCP or
 * design-context dumps. The backend rejects oversized provenance to avoid
 * Firestore document bloat.
 */
export interface GeneratedThemeV2FigmaProvenance {
  fileKey?: string;
  fileName?: string;
  pageName?: string;
  frameName?: string;
  nodeIds?: string[];
  /** Audit-only Figma URL; the server never fetches it. */
  sourceUrl?: string;
  variableRefs?: string[];
  styleRefs?: string[];
}

/**
 * Metadata describing how a theme was generated or projected from references.
 * Kept optional so existing themes remain valid.
 */
export interface GeneratedThemeV2Metadata {
  sourceKind: 'image-analysis' | 'manual' | 'import' | 'figma-frame' | 'unknown';
  sourceAssetIds?: string[];
  sourceImageUrls?: string[];
  analysisId?: string;
  generatedAt?: string;
  generatorVersion?: string;
  provider?: string;
  model?: string;
  confidence?: number;
  warnings?: string[];
  /** Stable artifact hash for idempotent re-import (sourceKind 'figma-frame'). */
  artifactHash?: string;
  /** Compact Figma provenance when sourceKind === 'figma-frame'. */
  figma?: GeneratedThemeV2FigmaProvenance;
  /**
   * Compact audit trail from the server-side image analysis response.
   * This lets generated drafts expose what the model returned before local
   * Theme V2 projection/normalization is reviewed and applied.
   */
  analysisSummary?: string;
  screenInventoryCount?: number;
  evidenceCount?: number;
  measurementBasis?: Record<string, unknown>;
  projectionPatch?: Record<string, unknown>;
}

// ============================================================================
// Theme V2 Animation Settings (schema 2.17 motion, theme plane)
// ============================================================================

/**
 * Curated motion scheme ids selectable in the theme editor.
 * `custom` is implicit — assigned when Advanced values diverge from every scheme.
 */
export type ThemeV2MotionPresetId =
  | 'none'
  | 'subtle'
  | 'smooth'
  | 'dynamic'
  | 'playful'
  | 'custom';

/**
 * Per-StepKind override of the theme-wide motion values
 * (e.g. Paywall: calm coverVertical + fade instead of a bouncy scheme).
 */
export interface ThemeV2AnimationPerKindOverride {
  transition?: ScreenTransition | null;
  screenAnimation?: ScreenAnimation | null;
}

/**
 * Theme-level animation settings (motion scheme).
 *
 * Materialization (owned by ThemeV2EditorView cascade + theme apply):
 * - `transition`      → theme.flowDocument.defaultTransition → version screensGraph.defaultTransition
 * - `screenAnimation` → theme.flowDocument.defaultScreenAnimation + every template
 *                       screen's `content.screenAnimation` (tiles stay WYSIWYG)
 * - `perKindOverrides[kind].transition` → theme.flowDocument.perKindTransitions →
 *                       version screensGraph.perKindTransitions (runtime resolves
 *                       route.transition > perKindTransitions[targetKind] > defaultTransition)
 * - `perKindOverrides[kind].screenAnimation` → that kind's template screens'
 *                       `content.screenAnimation` (wins over the theme-wide entrance)
 *
 * Absent field / absent settings object = legacy behavior (no theme-driven motion).
 *
 * DEFERRED SCOPE (deliberate v1 cut, user decision 2026-07-06): options list
 * `itemAnimation`/`itemStagger` and button `enterAnimation`/`idleAnimation`/
 * `tapAnimation` are NOT part of the theme motion bundle — configure them
 * per-screen in the screen editor. See modules/theme-v2/MODULE.md → Animations.
 */
export interface ThemeV2AnimationSettings {
  /** Selected curated scheme; drives the card UI. Values below are the truth. */
  motionPreset?: ThemeV2MotionPresetId;
  /** Screen-to-screen transition (theme default). null/absent = no transition. */
  transition?: ScreenTransition | null;
  /** Screen entrance animation + stagger. null/absent = no entrance. */
  screenAnimation?: ScreenAnimation | null;
  /** Per-StepKind overrides (v1: Paywall calm override). */
  perKindOverrides?: Partial<Record<StepKind, ThemeV2AnimationPerKindOverride>>;
}

// ============================================================================
// ThemeV2 Interface
// ============================================================================

/**
 * Complete ThemeV2 entity interface.
 *
 * ThemeV2 is a template that defines visual settings for V2 onboarding screens.
 * When applied to an onboarding, settings propagate to all StepNode screens
 * via the V2 theme mapper using `.appearance.*` paths.
 *
 * Stored in Firestore at: themesV2/{themeId}
 */
export interface ThemeV2 {
  // === IDENTITY ===
  /** Unique theme identifier */
  id: string;
  /** Display name */
  name: string;

  // === V2 SCREEN-LEVEL SETTINGS ===
  /** Background configuration (solid, photo, or clip) — V2 Backdrop */
  background: Backdrop;
  /** Browser chrome color projected to each inherited screen; null/absent disables it */
  webViewBackgroundColor?: string | null;
  /** Header bar with skip/back/close buttons — V2 HeaderBar */
  headerBar: HeaderBar;
  /** Action bar with primary/secondary buttons — V2 ActionBar */
  actionBar: ActionBar;

  // === HIGH-LEVEL THEME SETTINGS ===
  /** Color palette for the theme */
  colors: ThemeV2Colors;
  /** Font configuration (using V2 TypographyStyle) */
  fonts: ThemeV2Fonts;
  /** Options/list appearance settings */
  optionsListSettings: ThemeV2OptionsListSettings;
  /** Layout, spacing, radius, and dimension tokens */
  layoutSettings?: ThemeV2LayoutSettings;
  /** Motion scheme (screen transition + entrance). Absent = legacy, no theme motion */
  animationSettings?: ThemeV2AnimationSettings;

  // === FLOW DOCUMENT ===
  /** V2 screen templates (FlowDocument with StepNode screens) */
  flowDocument: FlowDocument;

  // === METADATA ===
  /** ISO date string when theme was created */
  createdAt: string;
  /** ISO date string when theme was last updated */
  updatedAt: string;
  /** User ID who created the theme (optional) */
  createdBy?: string;

  // === ADMIN SETTINGS ===
  /** Display order in theme list (lower = first), admin-only */
  order?: number;
  /** Optional metadata for AI/image-derived themes */
  generatedMetadata?: GeneratedThemeV2Metadata;
}

// ============================================================================
// Default Values
// ============================================================================

/**
 * Default color palette for new V2 themes.
 */
export const DEFAULT_THEME_V2_COLORS: ThemeV2Colors = {
  buttonsBackground: '#ffc201',
  titlesSubtitles: '#ffffff',
  buttonsTitle: '#000000',
};

/**
 * Default font settings for new V2 themes.
 * Uses SF Pro as the default font family.
 *
 * Title: size 21, weight 700, color = Secondary
 * Subtitle: size 17, weight 500, color = 50% Secondary
 */
export const DEFAULT_THEME_V2_FONTS: ThemeV2Fonts = {
  main: { fontFamily: 'SF Pro' },
  title: {
    fontFamily: 'SF Pro',
    fontWeight: 700,
    fontSize: 21,
    align: 'center',
    // color is undefined = uses colors.titlesSubtitles (Secondary)
  },
  subtitle: {
    fontFamily: 'SF Pro',
    fontWeight: 500,
    fontSize: 17,
    align: 'center',
    // color is undefined = uses 50% colors.titlesSubtitles
  },
};

/**
 * Default options list settings for new V2 themes.
 *
 * CheckboxKind: circle (ToggleMarkKind)
 * ListBorderRadius: 12px
 * ItemTitle: size 15, weight 600
 * ItemSubtitle: size 15, weight 400
 */
export const DEFAULT_THEME_V2_OPTIONS_LIST_SETTINGS: ThemeV2OptionsListSettings = {
  checkboxKind: 'circle',
  listBorderRadius: 12,
  itemTitle: {
    fontFamily: 'SF Pro',
    fontWeight: 600,
    fontSize: 15,
    // color is undefined = uses colors.titlesSubtitles (Secondary)
  },
  itemSubtitle: {
    fontFamily: 'SF Pro',
    fontWeight: 400,
    fontSize: 15,
    // color is undefined = uses 50% colors.titlesSubtitles
  },
};

/**
 * Default layout token scale.
 *
 * Component-specific fields stay unset by default to preserve existing
 * rendering behavior. AI/image projection fills them only when evidence is
 * strong enough.
 */
export const DEFAULT_THEME_V2_LAYOUT_SETTINGS: ThemeV2LayoutSettings = {
  spacingScale: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
  },
  radiusScale: {
    sm: 4,
    md: 8,
    lg: 16,
    full: 9999,
  },
};

/**
 * Default V2 backdrop (solid dark background).
 */
export const DEFAULT_BACKDROP: Backdrop = {
  kind: 'solid',
  content: {
    color: '#1a1a1a',
  },
};

/**
 * Default V2 header bar (empty — no skip/back/close by default).
 */
export const DEFAULT_HEADER_BAR: HeaderBar = {};

/**
 * Default V2 action bar (empty — no primary/secondary buttons by default).
 */
export const DEFAULT_ACTION_BAR: ActionBar = {};

/**
 * Default empty FlowDocument for new V2 themes.
 * Uses 'en-US' as the default language.
 */
export const DEFAULT_FLOW_DOCUMENT: FlowDocument = {
  schemaVersion: '2.12.0',
  defaultLanguage: 'en-US',
  languages: ['en-US'],
  launchScreenId: '',
  screens: {},
  metadata: {},
};

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Input type for creating a new V2 theme.
 * Omits auto-generated fields (id, createdAt, updatedAt).
 */
export type CreateThemeV2Input = Partial<Omit<ThemeV2, 'id' | 'createdAt' | 'updatedAt'>>;

/**
 * Input type for updating an existing V2 theme.
 * Omits immutable fields (id, createdAt).
 */
export type UpdateThemeV2Input = Partial<Omit<ThemeV2, 'id' | 'createdAt'>>;

// Re-export V2 schema types for convenience
export type {
  Backdrop,
  HeaderBar,
  ActionBar,
  FlowDocument,
  TypographyStyle,
  FontFamily,
  ToggleMarkKind,
  EdgeInsets,
  ScreenAnimation,
  ScreenTransition,
};
